import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import { sendCollectorOtp, verifyCollectorOtp, listCollectorItems } from "../../services/collectorApi";
import {
  isCollectorEnabled,
  isCollectorLocalDevHint,
  safeCollectorNext,
  setCollectorToken,
  takeCollectorSessionEndReason,
} from "../../helpers/collectorSession";
import { defaultCollectionPath } from "../../helpers/collectorRooms";

const LOCAL_TEST_PHONE = "8935017347";
const COUNTRY_CODES = ["+91", "+1", "+44", "+971", "+65"];

const Req = () => <span className="su-req" aria-hidden="true">*</span>;

const CollectorLogin: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const next = safeCollectorNext(new URLSearchParams(location.search).get("next"));
  const signOutIncomplete = Boolean((location.state as { signOutIncomplete?: boolean } | undefined)?.signOutIncomplete);
  // Read once: why the last session ended, when the person did not log out themselves.
  const [endedBecause] = useState(takeCollectorSessionEndReason);
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const showDevHint = isCollectorLocalDevHint();
  const [phone, setPhone] = useState(showDevHint ? LOCAL_TEST_PHONE : "");
  const [countryCode, setCountryCode] = useState("+91");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = window.setTimeout(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  // Each step's field takes focus when it appears.
  useEffect(() => {
    const id = { phone: "collectorPhone", otp: "collectorOtp" }[step];
    document.getElementById(id)?.focus();
  }, [step]);

  if (!isCollectorEnabled()) {
    return (
      <div className="su__card">
        <h1>My Collection</h1>
        <p className="su__lead">My Collection is not available yet.</p>
        <p className="su__alt"><Link to="/login">Studio log in</Link></p>
      </div>
    );
  }

  const fullPhone = () => {
    const digits = phone.replace(/\D/g, "");
    const cc = countryCode.replace(/\D/g, "");
    if (digits.length === 10 && cc === "91") return digits;
    return cc + digits;
  };

  const sendOtp = async () => {
    setError("");
    if (phone.replace(/\D/g, "").length < 10) {
      setError("Enter a valid phone number.");
      return;
    }
    setLoading(true);
    try {
      await sendCollectorOtp(fullPhone());
      setStep("otp");
      setResendIn(30);
      notifications({ title: "OTP sent", message: "Check your phone", type: "success" });
    } catch (e: any) {
      setError(e?.response?.data?.message || "Could not send the OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const finishLogin = async (token: string, refreshToken?: string) => {
    setCollectorToken(token, refreshToken);
    notifications({
      title: "Welcome",
      message: "Signed in to My Collection",
      type: "success",
    });
    if (next) {
      history.push(next);
      return;
    }
    try {
      const itemsRes = await listCollectorItems();
      history.push(defaultCollectionPath(itemsRes.data?.data?.items || []));
    } catch {
      history.push("/my-collection");
    }
  };

  const submitOtp = async () => {
    setError("");
    if (otp.trim().length < 4) {
      setError("Enter the 4-digit OTP.");
      return;
    }
    setLoading(true);
    try {
      const res = await verifyCollectorOtp(fullPhone(), otp.trim());
      const token = res.data?.data?.token;
      if (!token) {
        setError("Could not sign in.");
        return;
      }
      // No name step: a name can be added later in Settings, so the first visit stays short.
      await finishLogin(token, res.data?.data?.refreshToken);
    } catch (e: any) {
      setError(e?.response?.data?.message || "That OTP didn't match. Check it and try again.");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setLoading(true);
    setError("");
    try {
      await sendCollectorOtp(fullPhone());
      setResendIn(30);
      notifications({ title: "OTP sent", message: "Check your phone", type: "success" });
    } catch {
      setError("Could not resend the OTP.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (action: () => void) => (event: React.FormEvent) => {
    event.preventDefault();
    if (!loading) action();
  };

  const errorBanner = error && <p className="su-banner" role="alert">{error}</p>;
  const devHint = showDevHint && phone === LOCAL_TEST_PHONE && (
    <p className="su-hint">Local test number is prefilled. OTP: 1234</p>
  );

  return (
    <div className="su__card">
      <span className="s-eyebrow">My Collection</span>

      {step === "phone" && (
        <>
          <h1>Log in or sign up</h1>
          <p className="su__lead">Your phone number is all you need. New here? The OTP creates your free collection.</p>
          {signOutIncomplete && (
            <p className="su-banner" role="status">
              You are logged out on this device. We could not reach our server, so other phones or browsers may still be
              signed in. To end those too, log in and tap Log out again when you are online.
            </p>
          )}
          {endedBecause === "number-changed" && (
            <p className="su-banner" role="status">
              Your number changed. Please log in again with your new number.
            </p>
          )}
          {endedBecause === "refused" && (
            <p className="su-banner" role="status">
              You were logged out. This happens after a long time away, or when your number changed or you logged out
              on another phone. Please log in again; your collection is safe.
            </p>
          )}
          {devHint}
          {errorBanner}
          <form noValidate onSubmit={onSubmit(sendOtp)}>
            <div className={`su-field${error ? " has-error" : ""}`}>
              <label htmlFor="collectorPhone">Phone<Req /></label>
              <div className="su-phone">
                <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} aria-label="Country code">
                  {COUNTRY_CODES.map((code) => <option key={code} value={code}>{code}</option>)}
                </select>
                <input
                  id="collectorPhone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="10-digit mobile"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                    setError("");
                  }}
                  required
                  aria-invalid={Boolean(error)}
                />
              </div>
            </div>
            <button type="submit" className="s-btn s-btn--primary su__submit" disabled={loading} aria-busy={loading}>
              {loading ? "Sending OTP…" : "Send OTP"}
            </button>
          </form>
          <p className="su__alt">
            Make or sell handmade work? <Link to="/login">Studio log in</Link>
          </p>
        </>
      )}

      {step === "otp" && (
        <>
          <h1>Enter the OTP</h1>
          <p className="su__lead">
            We sent a 4-digit code to <b>{countryCode} {phone}</b>.{" "}
            <button type="button" className="su-link" onClick={() => { setStep("phone"); setOtp(""); setError(""); }}>
              Change number
            </button>
          </p>
          {devHint}
          {errorBanner}
          <form noValidate onSubmit={onSubmit(submitOtp)}>
            <div className={`su-field${error ? " has-error" : ""}`}>
              <label htmlFor="collectorOtp">One-time password<Req /></label>
              <input
                id="collectorOtp"
                className="su-otp"
                value={otp}
                maxLength={4}
                inputMode="numeric"
                autoComplete="one-time-code"
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, ""));
                  setError("");
                }}
                required
                aria-invalid={Boolean(error)}
              />
            </div>
            <button type="submit" className="s-btn s-btn--primary su__submit" disabled={loading} aria-busy={loading}>
              {loading ? "Checking…" : "Verify & continue"}
            </button>
          </form>
          <p className="su__alt">
            Didn't get it?{" "}
            <button type="button" className="su-link" onClick={resendOtp} disabled={loading || resendIn > 0}>
              {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend OTP"}
            </button>
          </p>
        </>
      )}
    </div>
  );
};

export default CollectorLogin;
